import React, { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import { createColumnHelper } from '@tanstack/react-table';
import { format } from 'date-fns';

import { type TypeSharedModel } from '../../common/dataTypes';
import { createTanstackTable } from '../../common/utils';
import { useGetSharedModelSharingInvitations } from '../../hooks/tanstack/useSharedModelsHook';
import {
  useGetDistinguishModel,
  useGetRouteParams,
  useGetSearchResults,
} from '../../hooks/tanstack/useUtilsHook';
import { type SharingInvitationStatus } from '../../types';
import cn from '../../utils/cn';
import SearchInput from '../SearchInput';
import TanstackTable from '../TanstackTable';

export type DataRow = {
  id: number;
  sharedBy: {
    email: string;
    full_name: string;
  };
  sharedTo: {
    email: string;
  };
  status: SharingInvitationStatus;
  createdAt: string;
  acceptedAt: string;
};

const columnHelper = createColumnHelper<DataRow>();

const columns = [
  columnHelper.accessor('sharedBy', {
    id: 'sharedBy',
    cell: (info) =>
      info.getValue() ? (
        <div className="w-full">{info.getValue().full_name}</div>
      ) : (
        <div className="text-gray-400 w-full text-start">--</div>
      ),
    header: () => <div>Shared By</div>,
  }),
  columnHelper.accessor('sharedTo', {
    id: 'sharedTo',
    cell: (info) => {
      const receiver = info.getValue();

      return receiver ? (
        <div className="w-full">{receiver.email}</div>
      ) : (
        <div className="text-gray-400 w-full text-center">--</div>
      );
    },
    header: () => <div>Shared To</div>,
  }),
  columnHelper.accessor('createdAt', {
    id: 'createdAt',
    cell: (info) => {
      const value = info.getValue();

      return value ? (
        <div className="w-full">
          {format(value as string, 'y/MM/dd h:mm a')}
        </div>
      ) : (
        <div className="text-gray-400 w-full text-center">--</div>
      );
    },
    sortingFn: (a, b) =>
      new Date(a.original.createdAt as string).getTime() -
      new Date(b.original.createdAt as string).getTime(),
    header: () => <div>Date Sent</div>,
  }),
  columnHelper.accessor('status', {
    id: 'status',
    cell: (info) => {
      const status = info.getValue();

      const StatusLabel = ({
        label,
        value,
      }: {
        value: string;
        label: string;
      }) => (
        <div className="flex justify-start py-2">
          <p
            className={cn('w-fit rounded-full bg-[#E1FBE7] px-6 py-1 ', {
              'bg-[#EEF6FF] text-green-600': value === 'approved',
              'bg-[#EEF6FF] text-[#117DD3]': value === 'accepted',
              'text-[#C38D04] bg-[#FFF8E0]':
                value === 'waiting-for-acceptance' ||
                value === 'waiting-for-approval',
              'bg-[#FFF4F2] text-primary-red':
                value === 'disapproved' || value === 'rejected',
            })}
          >
            {label}
          </p>
        </div>
      );

      return status ? (
        <div className="w-full">
          {status === 'approved' ? (
            <StatusLabel value={status} label="Approved" />
          ) : null}
          {status === 'waiting-for-acceptance' ? (
            <StatusLabel value={status} label="Pending" />
          ) : null}
          {status === 'waiting-for-approval' ? (
            <StatusLabel value={status} label="Pending" />
          ) : null}
          {status === 'disapproved' ? (
            <StatusLabel value={status} label="Disapproved" />
          ) : null}
          {status === 'accepted' ? (
            <StatusLabel value={status} label="Accepted" />
          ) : null}
          {status === 'rejected' ? (
            <StatusLabel value={status} label="Rejected" />
          ) : null}
        </div>
      ) : (
        <div className="text-gray-400 w-full text-center">--</div>
      );
    },
    header: () => <div>Status</div>,
  }),
  columnHelper.accessor('acceptedAt', {
    id: 'acceptedAt',
    cell: (info) => {
      const value = info.getValue();

      return value ? (
        <div className="w-full">
          {format(value as string, 'y/MM/dd h:mm a')}
        </div>
      ) : (
        <div className="text-gray-400 w-full text-center">--</div>
      );
    },
    sortingFn: (a, b) =>
      new Date(a.original.acceptedAt as string).getTime() -
      new Date(b.original.acceptedAt as string).getTime(),
    header: () => <div>Date Accepted</div>,
  }),
];

const SharedModelSharingInvitationsView = () => {
  const [data, setData] = useState<DataRow[]>([]);

  const { modelId, orgId, projectId } = useGetRouteParams();

  const form = useForm();

  const { watch } = form;

  const {
    data: sharedModelSharingInvitations,
    isLoading: isFetchingSharedModelSharingInvitations,
  } = useGetSharedModelSharingInvitations(
    {
      modelId,
      orgId,
      projectId,
    },
    {
      enabled: !!modelId && !!orgId && !!projectId,
    }
  );

  const { model, isLoading } = useGetDistinguishModel();

  const searchText = watch('search');

  const searchResults =
    useGetSearchResults<DataRow>({
      searchValue: searchText,
      data,
      filterFields: [
        'sharedTo.email',
        'sharedBy.email',
        'sharedBy.full_name',
        'status',
      ],
    }) ?? data;

  useEffect(() => {
    if (sharedModelSharingInvitations && model) {
      setData(
        sharedModelSharingInvitations.map((r) => ({
          id: r.id,
          sharedBy: {
            email: (model as TypeSharedModel).shared_by.email,
            full_name: (model as TypeSharedModel).shared_by.full_name,
          },
          sharedTo: {
            email: r.email,
          },
          status: r.status,
          createdAt: r.created_at,
          acceptedAt: r.accepted_at,
        })) as DataRow[]
      );
    }
  }, [sharedModelSharingInvitations, model]);

  const table = createTanstackTable<DataRow>({
    data: searchResults,
    columns,
  });

  return (
    <div className="flex flex-col gap-6">
      <h1 className="text-2xl font-bold">Shared</h1>
      <FormProvider {...form}>
        <form className="flex justify-between">
          <div className="flex w-[300px] gap-2">
            <SearchInput />
          </div>
        </form>
      </FormProvider>
      <TanstackTable
        table={table}
        isLoading={isLoading || isFetchingSharedModelSharingInvitations}
        emptyState={searchText ? 'No results' : 'No available records.'}
      />
    </div>
  );
};

export default SharedModelSharingInvitationsView;
